import { ChangeDetectionStrategy, Component, forwardRef, input, signal, effect, ChangeDetectorRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'zag-checkbox',
  imports: [],
  templateUrl: './checkbox.html',
  styleUrl: './checkbox.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: inline-flex; align-self: flex-start;',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Checkbox),
      multi: true,
    },
  ],
})
export class Checkbox implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  // Input properties with signals
  readonly disabled = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly label = input<string>('');

  // Internal state
  protected readonly checked = signal<boolean>(false);
  protected readonly focused = signal<boolean>(false);

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: boolean) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    // Sync disabled state effect
    effect(() => {
      if (this.disabled()) {
        this.cdr.markForCheck();
      }
    });
  }

  writeValue(value: boolean): void {
    this.checked.set(value ?? false);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.checked;
    this.checked.set(newValue);
    this.onChange(newValue);
  }

  protected onBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  protected onFocus(): void {
    this.focused.set(true);
  }
}
